import { Collapse, Form, Input, message, Tooltip } from "antd";
import { useContext, useMemo, useRef, useState } from "react";
import { sortLinkedItems, validatorForFieldValue } from "utils/utils";
import { Link, useParams } from "react-router-dom";
import { ErrorMessages, UserParticipationType } from "constants/index";
import { RiskLevelType } from "components/date-block/models";
import linkedCell from "components/links/linkedCell";
import LinkIcon from "components/svg-icons/link-icon";
import AsterixIcon from "components/svg-icons/asterix-icon";
import {
  EditOutlined,
  InfoCircleOutlined,
  SwapOutlined
} from "@ant-design/icons";
import { ProjectContext } from "context/ProjectProvider";
import { InfoFeatureType } from "change-events/change-event-polling";
import { FeatureTypes } from "entity-app/constants";
import { useSubmmittialDetails } from "pages/submittal-details/new-submittal-details/hook/submittal-details-hook";
import { specSectionLabel } from "components/cell-renders";

// Helper component for the linking action button
function LinkingActionButton({
  projectId,
  featureInstanceData,
  linkText,
  testId,
  isMaterialsFeature
}: {
  projectId: string;
  featureInstanceData: any;
  linkText: string;
  testId: string;
  isMaterialsFeature: boolean;
}) {
  if (isMaterialsFeature) {
    return (
      <Link
        to={`/project/${projectId}/schedule/material-linking?&entityId=${featureInstanceData?.id}&source=material`}
        data-testid={testId}
      >
        {linkText}
      </Link>
    );
  }
  return (
    <Link
      to={`/project/${projectId}/schedule/submittal-linking?&SubmittalId=${featureInstanceData?.id}`}
      data-testid={testId}
    >
      {linkText}
    </Link>
  );
}

// Helper component for empty materials state
function EmptyMaterialsState({
  featureInstanceData,
  submittalInfo,
  projectId,
  canEditLinkingPage,
  isRevisionMode,
  isVoidSubmittal,
  isDeletedSubmittal
}: {
  featureInstanceData: any;
  submittalInfo: any;
  projectId: string;
  canEditLinkingPage: boolean;
  isRevisionMode: boolean;
  isVoidSubmittal: boolean;
  isDeletedSubmittal: boolean;
}) {
  const isMaterialsFeature =
    featureInstanceData?.feature_type_id === FeatureTypes.MATERIALS;
  const canShowLink = isMaterialsFeature
    ? canEditLinkingPage
    : canEditLinkingPage &&
      !isRevisionMode &&
      !isVoidSubmittal &&
      !isDeletedSubmittal;

  const getMessageAndLinkText = () => {
    if (isMaterialsFeature) {
      return {
        messageText: "No links added",
        linkText: "Click to add"
      };
    }

    return {
      messageText: submittalInfo?.material_tracking
        ? "No links added"
        : "Material tracking is disabled",
      linkText: submittalInfo?.material_tracking
        ? "Click to add"
        : "Click here to enable"
    };
  };

  const { messageText, linkText } = getMessageAndLinkText();

  return (
    <span className="text-xs text-neutral-400">
      {messageText}{" "}
      {canShowLink && (
        <LinkingActionButton
          projectId={projectId}
          featureInstanceData={featureInstanceData}
          linkText={linkText}
          testId="featurelinkedmaterials-link-enable-material-tracking"
          isMaterialsFeature={isMaterialsFeature}
        />
      )}
    </span>
  );
}

export default function FeatureMaterialsLinks(props: {
  featureInstanceData: any;
  canEditLinkingPage: boolean;
  actions: any;
  isRevisionMode: boolean;
  canEditMaterialFields: boolean;
  isVoidSubmittal: boolean;
  isDeletedSubmittal: boolean;
}) {
  const {
    featureInstanceData,
    canEditLinkingPage,
    actions,
    isRevisionMode,
    canEditMaterialFields,
    isVoidSubmittal,
    isDeletedSubmittal
  } = props;

  const { projectId } = useParams() as any;
  const cssForValue = "text-xs font-normal text-black text-opacity-80";
  const { Panel } = Collapse;
  const { tokenRetrievalState, projectDetails } = useContext(ProjectContext);

  const [isMaterialExist, setMaterialExist] = useState(false);
  const [isEditable, setEditable] = useState(false);
  const inputRef: any = useRef();
  const [materialName, setMaterialName] = useState("");
  const { submittalParticipants } = useSubmmittialDetails({
    featureInstanceData,
    isRevisionMode
  });

  const submittalInfo = useMemo(() => {
    if (!featureInstanceData?.feature_submittals?.length) return null;
    return featureInstanceData?.feature_submittals[0];
  }, [featureInstanceData]);

  const materialLinks = useMemo(() => {
    if (featureInstanceData?.feature_type_id === FeatureTypes.MATERIALS) {
      const {
        predecessor_material_links: predecessorMaterialLinks,
        successor_material_links: successorMaterialLinks
      } = featureInstanceData;

      // Early return if no links exist
      if (
        !predecessorMaterialLinks?.length &&
        !successorMaterialLinks?.length
      ) {
        return [];
      }

      // Process predecessor materials with optimized mapping
      const predecessorMaterials =
        predecessorMaterialLinks?.map((link: any) => ({
          ...link?.source_feature_instance,
          predecessor_material: true,
          governing_link: link?.governing_link
        })) || [];

      // Process successor materials with optimized mapping
      const successorMaterials =
        successorMaterialLinks?.map((link: any) => ({
          ...link?.target_feature_instance,
          predecessor_material: false,
          governing_link: link?.governing_link
        })) || [];

      // Combine arrays efficiently
      const allMaterials = predecessorMaterials.concat(successorMaterials);

      return sortLinkedItems(allMaterials, "auto_inc_value");
    }
    const linksAsSource = featureInstanceData?.links_as_source;
    if (!linksAsSource?.length) {
      return [];
    }

    const mappedItems = linksAsSource
      .filter(
        (link: any) =>
          link?.target_feature_instance?.feature_type_id ===
          FeatureTypes.MATERIALS
      )
      .map((link: any) => ({
        ...link?.target_feature_instance,
        governing_link: link?.governing_link
      }));

    if (!mappedItems.length) {
      return [];
    }

    if (mappedItems.length > 1) {
      const nonImplicitLinks = mappedItems.filter(
        (link: any) => !link?.implicit
      );
      return sortLinkedItems(nonImplicitLinks, "auto_inc_value");
    }

    return sortLinkedItems(mappedItems, "auto_inc_value");
  }, [featureInstanceData]);

  const getShadowMaterialName = (link: any) => {
    const showSpecNo = projectDetails?.spec_section;
    const specSectionObj = link?.feature_instance_spec_sections.length
      ? link?.feature_instance_spec_sections[0]?.spec_section
      : null;

    const submittalRefrenceId =
      showSpecNo && specSectionObj?.section_number
        ? `${specSectionObj?.section_number} - ${featureInstanceData?.auto_inc_value}`
        : featureInstanceData?.auto_inc_value;

    const updateImplicitMaterialName = async () => {
      const submitter = featureInstanceData?.feature_user_paticipants?.find(
        (user: any) =>
          user?.user_participation_type?.id === UserParticipationType.SUBMITTER
      );

      const specSection = featureInstanceData?.feature_instance_spec_sections
        ?.length
        ? featureInstanceData?.feature_instance_spec_sections[0]?.spec_section
        : null;

      const mapObj: any = {
        id: link.id,
        title: inputRef?.current?.input?.value || "",
        assignee_unregistered:
          submitter?.user_unregistered ||
          (!submitter?.user &&
            `${submitter?.user_source_info?.first_name} ${submitter?.user_source_info?.last_name}`) ||
          null,
        assignee: null,
        responsible_contractor:
          submittalParticipants?.contractor?.vendor_id || null,
        spec_sections: specSection ? [specSection?.id] : null
      };

      if (!mapObj.assignee_unregistered) {
        mapObj.assignee = submittalParticipants?.submitter?.user?.id;
      }

      const payload = {
        variables: {
          feature_instance_type_id: FeatureTypes.MATERIALS,
          feature_instances: [mapObj]
        }
      };

      const updateResponse = await actions.updateFeatureInstancesResponse(
        payload
      );

      const updateResponseMsg =
        updateResponse?.data?.update_feature_instances?.message;

      if (updateResponseMsg === "Feature instances updated") {
        message.success("Updated Successfully.");
      }
    };

    return (
      <div className="w-full material-name-input">
        <div className="flex w-full truncate px-1  items-center">
          {isEditable ? (
            <Input
              bordered={isEditable}
              className="material-name-input"
              ref={inputRef}
              autoFocus
              defaultValue={`Material for Submittal ${
                submittalRefrenceId ?? ""
              }`}
              readOnly={!isEditable}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.code === "Escape") {
                  setEditable(false);
                  setMaterialExist(false);
                  setMaterialName(
                    `Material for Submittal ${submittalRefrenceId ?? ""}`
                  );
                }
              }}
              onPressEnter={() => {
                if (!isMaterialExist) {
                  setEditable(false);
                  setMaterialExist(false);
                  setMaterialName(inputRef?.current?.input?.value || "");
                  updateImplicitMaterialName();
                }
              }}
              onBlur={() => {
                setEditable(false);
                setMaterialExist(false);
                setMaterialName(
                  `Material for Submittal ${submittalRefrenceId ?? ""}`
                );
              }}
              onInput={async (name) => {
                if (name.currentTarget.value.trim() === "") return;
                try {
                  await validatorForFieldValue(
                    "title",
                    name.currentTarget?.value?.trim(),
                    InfoFeatureType.material,
                    tokenRetrievalState?.token,
                    "Material exist."
                  );
                  setMaterialExist(false);
                } catch (error) {
                  if (error === "Material exist.") {
                    setMaterialExist(true);
                  }
                }
              }}
              data-testid="material-name-input"
            />
          ) : (
            <Tooltip
              title={
                materialName?.trim() !== ""
                  ? materialName
                  : `Material for Submittal ${submittalRefrenceId ?? ""}`
              }
            >
              <span className="px-4 w-16 py-1 grow truncate">
                {materialName?.trim() !== ""
                  ? materialName
                  : `Material for Submittal ${submittalRefrenceId ?? ""}`}
              </span>
            </Tooltip>
          )}
          {!isVoidSubmittal &&
            !isDeletedSubmittal &&
            canEditMaterialFields &&
            !isEditable && (
              <EditOutlined
                className="w-6"
                onClick={() => {
                  setEditable(true);
                  inputRef?.current?.focus();
                }}
              />
            )}
        </div>
        {isMaterialExist && (
          <span className="text-xs text-red-600 ml-1">
            {ErrorMessages.MaterialExists}
          </span>
        )}
      </div>
    );
  };

  const materialPanelHeader = (link: any) => {
    const matFullName = link.auto_inc_value
      ? `${link.auto_inc_value}-${link.title}`
      : link.title;
    const title: string = link.implicit ? "" : matFullName;
    return (
      <div className="flex items-center" title={title}>
        <span className="flex items-center mr-2">
          <LinkIcon />
        </span>

        {link.implicit ? (
          getShadowMaterialName(link)
        ) : (
          <>
            {link?.auto_inc_value && (
              <i className="opacity-60">
                {link?.auto_inc_value}
                {!link.governing_link && <span className="mx-1">-</span>}
                {link.governing_link && link.predecessor_material && (
                  <span className="mx-1">-</span>
                )}
              </i>
            )}
            {link.governing_link && !link.predecessor_material && (
              <span className="flex items-center ml-2 mr-1 mb-1">
                <AsterixIcon />
              </span>
            )}
            <span className="w-full truncate overflow-hidden mr-5">
              {link.title}
            </span>
            {link?.predecessor_material && (
              <div className="flex items-center bg-ciq-gray-500 rounded h-6 space-x-1 px-2 w-[196px] mr-6">
                <span className="flex items-center">
                  <SwapOutlined className="text-ciq-gray-550" />
                </span>
                <span className="flex items-center font-normal text-xs">
                  Predecessor
                </span>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <Form layout="vertical" data-testid="fml-form">
      <Form.Item
        label={
          <span>
            Materials
            {materialLinks?.length ? ` (${materialLinks?.length})` : ""}
            {featureInstanceData?.feature_type_id === 2 && (
              <Tooltip title="Materials shown below are Successors of this Material, unless mentioned.">
                <InfoCircleOutlined
                  data-testid="material-info-tooltip"
                  className="pl-[3px]"
                  style={{ color: "#626262" }}
                />
              </Tooltip>
            )}
          </span>
        }
      >
        {materialLinks?.length > 0 ? (
          <Collapse
            accordion
            expandIconPosition="end"
            className="custom-collapse"
            data-testid="fml-collapse"
          >
            {materialLinks?.map((link: any) => {
              if (!link)
                return (
                  <Panel
                    header={
                      <Tooltip
                        title={
                          !link.implicit
                            ? ErrorMessages.MaterialAccessDenied
                            : ""
                        }
                      >
                        {materialPanelHeader(link)}
                      </Tooltip>
                    }
                    key={link.id}
                    className="custom-panel"
                    collapsible="disabled"
                  />
                );

              const materialReleaseStatus =
                link?.feature_workflow_instance?.milestone_state || "";
              const materialRiskLevel =
                link?.feature_workflow_instance?.risk_level || "";

              const specSectionObj = link?.feature_instance_spec_sections.length
                ? link?.feature_instance_spec_sections[0]?.spec_section
                : null;

              const specSection = specSectionLabel({
                specNumber: specSectionObj?.section_number,
                specName: specSectionObj?.section_name ?? specSectionObj?.title
              });

              const additionalPanelProps: any = {};
              if (link.implicit) {
                additionalPanelProps.collapsible = "disabled";
              }
              return (
                <Panel
                  header={materialPanelHeader(link)}
                  key={link.id}
                  className={
                    materialRiskLevel === RiskLevelType.High
                      ? "custom-panel risk-panel"
                      : "custom-panel"
                  }
                  {...additionalPanelProps}
                >
                  <div className="space-y-2.5" data-testid="fml-panel-content">
                    <div className="grid grid-rows-3 -space-y-1">
                      <div className="flex w-full">
                        {linkedCell(
                          "Spec Section:",
                          `${specSection}`,
                          "",
                          `${cssForValue} max-w-[300px] truncate`
                        )}
                      </div>
                      <div className="flex w-full">
                        {linkedCell(
                          "Status:",
                          materialReleaseStatus,
                          "",
                          cssForValue
                        )}
                      </div>
                      <div className="flex w-full justify-between">
                        {linkedCell(
                          "Risk:",
                          materialRiskLevel,
                          "",
                          materialRiskLevel === RiskLevelType.High
                            ? "date-block-risk-high text-xs font-semibold"
                            : cssForValue
                        )}
                        {!isRevisionMode && !link?.implicit ? (
                          <Link
                            className="border-solid border-gray-300 hover:text-black hover:border-black border-[1px] rounded px-2 py-1 text-xs text-black"
                            to={{
                              pathname: `/project/${projectId}/materials/${link?.id}`
                            }}
                            data-testid="featurelinkedmaterials-link-view-material"
                          >
                            View Material
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </Panel>
              );
            })}
          </Collapse>
        ) : (
          <EmptyMaterialsState
            featureInstanceData={featureInstanceData}
            submittalInfo={submittalInfo}
            projectId={projectId}
            canEditLinkingPage={canEditLinkingPage}
            isRevisionMode={isRevisionMode}
            isVoidSubmittal={isVoidSubmittal}
            isDeletedSubmittal={isDeletedSubmittal}
          />
        )}
      </Form.Item>
    </Form>
  );
}
